import React, { useState } from 'react';
import { apiPost } from "../../services/api";
import '../styles/ImageUploader.css';

const ImageUploader = ({ onImageUploaded, multiple = false }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);

  const handleFileSelect = (event) => {
    setError('');
    
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    // Проверка типа файлов
    const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
    if (invalidFiles.length > 0) {
      setError('Можно загружать только изображения');
      return;
    }
    
    // Проверка размера файлов (максимум 5MB)
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Размер изображения не должен превышать 5MB');
      return;
    }
    
    setSelectedFiles(multiple ? files : [files[0]]);
    
    // Создаем превью для выбранных файлов
    const filePreviewPromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(filePreviewPromises).then(results => {
      setPreviews(multiple ? results : [results[0]]);
    });
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Выберите файл для загрузки');
      return;
    }
    
    setUploading(true);
    setProgress(0);
    setError('');
    
    try {
      const formData = new FormData();
      
      if (multiple) {
        selectedFiles.forEach(file => {
          formData.append('images', file);
        });
        
        const response = await apiPost(
          `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/block/upload-images`,
          formData,
          {
            headers: {
            },
          }
        );
        
        if (onImageUploaded && typeof onImageUploaded === 'function') {
          onImageUploaded(response.data.images.map(img => ({
            imageUrl: img.imageUrl,
            altText: img.originalname
          })));
        }
      } else {
        formData.append('image', selectedFiles[0]);
        
        const response = await apiPost(
          `${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/api/block/upload-image`,
          formData,
          {
            headers: {
            },
          }
        );
        
        if (onImageUploaded && typeof onImageUploaded === 'function') {
          onImageUploaded({
            imageUrl: response.data.imageUrl,
            altText: response.data.originalname
          });
        }
      }
      
      // Сбрасываем состояние после успешной загрузки
      setSelectedFiles([]);
      setPreviews([]);
      setProgress(0);
    } catch (err) {
      console.error('Ошибка при загрузке изображения:', err);
      setError(`Ошибка при загрузке: ${err.response?.data?.message || err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    if (uploading) return;
    
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setPreviews(prevPreviews => prevPreviews.filter((_, i) => i !== index));
  };

  return (
    <div className="image-uploader">
      <div className="upload-area">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          multiple={multiple}
          id="file-input"
          disabled={uploading}
        />
        <label htmlFor="file-input" className={uploading ? 'disabled' : ''}>
          <div className="upload-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path fill="none" d="M0 0h24v24H0z"/>
              <path d="M4 19h16v-7h2v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-8h2v7zm9-10v7h-2V9H6l6-6 6 6h-5z"/>
            </svg>
          </div>
          <span>{multiple ? 'Выберите изображения' : 'Выберите изображение'}</span>
        </label>
      </div>
      
      {error && <div className="upload-error">{error}</div>}
      
      {previews.length > 0 && (
        <div className="preview-container">
          {previews.map((preview, index) => (
            <div key={index} className="preview-item">
              <img src={preview} alt={`Preview ${index + 1}`} />
              <button 
                type="button" 
                className="remove-button" 
                onClick={() => removeFile(index)}
                disabled={uploading}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      
      {progress > 0 && uploading && (
        <div className="progress-container">
          <div className="progress-bar" style={{ width: `${progress}%` }} />
          <div className="progress-text">{progress}%</div>
        </div>
      )}
      
      <button
        className="upload-button"
        onClick={handleUpload}
        disabled={uploading || selectedFiles.length === 0}
      >
        {uploading ? 'Загрузка...' : 'Загрузить'}
      </button>
    </div>
  );
};

export default ImageUploader;